import type { RgbColor } from "utils/hslToRgb";
import type { ColorRooms, ColorRoomsRoom } from "./types";

// Soft at the layer's fill opacity, and apart from each other at a glance.
export const ROOM_PALETTE: RgbColor[] = [
  [59, 130, 246],
  [16, 185, 129],
  [245, 158, 11],
  [168, 85, 247],
  [236, 72, 153],
  [20, 184, 166],
  [234, 88, 12],
  [99, 102, 241],
];

interface Box {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

const boxOf = (room: ColorRoomsRoom): Box => ({
  minX: Math.min(...room.points.map((at) => at.x)),
  minY: Math.min(...room.points.map((at) => at.y)),
  maxX: Math.max(...room.points.map((at) => at.x)),
  maxY: Math.max(...room.points.map((at) => at.y)),
});

// Rooms on a floor plan share walls, so boxes that meet within a wall's width are neighbours.
const touches = (a: Box, b: Box, distance: number): boolean =>
  a.minX - distance <= b.maxX &&
  b.minX - distance <= a.maxX &&
  a.minY - distance <= b.maxY &&
  b.minY - distance <= a.maxY;

export const colorRooms: ColorRooms = ({ rooms, touchDistance }) => {
  const usable = rooms.filter((room) => room.points.length >= 3);
  const boxes = new Map(usable.map((room) => [room.id, boxOf(room)]));
  // Biggest first: they have the most neighbours, so they choose while the palette is still free.
  const order = [...usable].sort((a, b) => {
    const area = (box: Box) => (box.maxX - box.minX) * (box.maxY - box.minY);
    return area(boxes.get(b.id)!) - area(boxes.get(a.id)!) || a.id - b.id;
  });
  const indexById = new Map<number, number>();
  order.forEach((room) => {
    const taken = new Set(
      order.flatMap((other) =>
        other.id !== room.id &&
        indexById.has(other.id) &&
        touches(boxes.get(room.id)!, boxes.get(other.id)!, touchDistance)
          ? [indexById.get(other.id)!]
          : [],
      ),
    );
    const free = ROOM_PALETTE.findIndex((_, index) => !taken.has(index));
    indexById.set(room.id, free === -1 ? room.id % ROOM_PALETTE.length : free);
  });
  return new Map([...indexById.entries()].map(([id, index]) => [id, ROOM_PALETTE[index]]));
};
